'use client';

import {
  ChevronDownIcon,
  ChevronUpIcon,
  SwitchIcon,
} from '@otter-ai/den-icons';
import {
  type Column,
  type ColumnDef,
  type FilterFnOption,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import {
  type CSSProperties,
  forwardRef,
  type ReactElement,
  useState,
} from 'react';
import { Button } from '../../components/Button';
import {
  type TableProps as BaseTableProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/Table';
import { cx } from '../../utils/cva';

export type { ColumnDef } from '@tanstack/react-table';
export { createColumnHelper } from '@tanstack/react-table';

export type DataTableProps<TData = unknown> = Omit<
  BaseTableProps,
  'children' | 'data-testid'
> & {
  columns: ColumnDef<TData>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  floating?: boolean;
  'data-testid': string | null;
  enablePagination?: boolean;
  pageSize?: number;
  globalFilter?: string;
  globalFilterFn?: FilterFnOption<TData>;
  cellClassName?: string;
  rowClassName?: (row: TData) => string;
  initialSorting?: SortingState;
  stickyHeader?: boolean;
  /** Pass any additional TanStack Table options */
  tableOptions?: Partial<Omit<TableOptions<TData>, 'data' | 'columns'>>;
};

/**
 * A data table component built on TanStack Table that provides a simplified API
 * for displaying tabular data with column definitions.
 * @beta
 * @example
 * const columns = [
 *   {
 *     accessorKey: 'name',
 *     header: 'Name',
 *   },
 *   {
 *     accessorKey: 'email',
 *     header: 'Email',
 *   },
 *   {
 *     id: 'role',
 *     accessorKey: 'role',
 *     header: 'Role',
 *     cell: (info) => info.getValue(),
 *   },
 * ];
 *
 * const data = [
 *   { name: 'John Doe', email: 'john@example.com', role: 'Admin' },
 *   { name: 'Jane Smith', email: 'jane@example.com', role: 'User' },
 * ];
 *
 * <DataTable columns={columns} data={data} bordered={true} />
 *
 * @param props.columns Column definitions (TanStack Table format)
 * @param props.data Array of data to display
 * @param props.onRowClick Optional row click handler - receives the clicked row data
 * @param props.floating Whether to use floating row style (no borders between rows)
 * @param props.bordered Whether to add a rounded border around the table
 * @param props.maxHeight Max height for the internal scroll container. Only applies when `stickyHeader` is true. Defaults to `70vh`.
 * @param props.cellClassName Custom className for table cells so you can override padding
 * @param props.initialSorting Initial sort state for the table
 * @param props.stickyHeader When true, the header row sticks to the top of the table scroll container so it remains visible while scrolling through rows.
 * @param props.data-testid The data-testid attribute
 */
export const DataTable = forwardRef<HTMLTableElement, DataTableProps>(
  (props, ref) => {
    const {
      columns,
      data,
      onRowClick,
      floating = false,
      bordered = false,
      'data-testid': dataTestId,
      className,
      maxHeight,
      enablePagination = false,
      pageSize = 10,
      globalFilter,
      globalFilterFn,
      cellClassName,
      rowClassName,
      initialSorting = [],
      stickyHeader = false,
      tableOptions,
      ...tableProps
    } = props;

    const finalTestId = !dataTestId ? 'data-table' : dataTestId;

    const [sorting, setSorting] = useState<SortingState>(initialSorting);
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize,
    });

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      ...(enablePagination && {
        getPaginationRowModel: getPaginationRowModel(),
      }),
      enableSortingRemoval: false,
      ...(globalFilterFn && { globalFilterFn }),
      state: {
        sorting,
        ...(globalFilter !== undefined && { globalFilter }),
        ...(enablePagination && { pagination }),
      },
      onSortingChange: setSorting,
      ...(enablePagination && { onPaginationChange: setPagination }),
      ...tableOptions,
    });

    const getLeftPinnedStyle = (column: Column<unknown>): CSSProperties => {
      if (column.getIsPinned() !== 'left') {
        return {};
      }

      return {
        left: column.getStart('left'),
      };
    };

    return (
      <div className="flex flex-col gap-4">
        <Table
          ref={ref}
          data-testid={dataTestId}
          className={className}
          maxHeight={stickyHeader ? (maxHeight ?? '70vh') : undefined}
          variant={floating ? 'floating' : 'default'}
          bordered={bordered}
          {...tableProps}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const isSorted = header.column.getIsSorted();
                  const isLeftPinned = header.column.getIsPinned() === 'left';

                  const isStringHeader =
                    typeof header.column.columnDef.header === 'string';
                  const shouldUseButton = isStringHeader && canSort;
                  const sortIcons = canSort && (
                    <>
                      {!isSorted && (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <SwitchIcon size={20} aria-label="Sortable column" />
                        </div>
                      )}
                      {isSorted === 'asc' && (
                        <ChevronUpIcon
                          size={20}
                          aria-label="Sorted ascending"
                        />
                      )}
                      {isSorted === 'desc' && (
                        <ChevronDownIcon
                          size={20}
                          aria-label="Sorted descending"
                        />
                      )}
                    </>
                  );

                  return (
                    <TableHead
                      key={header.id}
                      data-testid={`${finalTestId}-header-${header.id}`}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      className={cx(
                        stickyHeader && 'sticky top-0 z-20 bg-default',
                        canSort && 'cursor-pointer select-none',
                        isLeftPinned &&
                          'sticky z-20 bg-default border-r border-subtle',
                        stickyHeader && isLeftPinned && 'z-30',
                      )}
                      style={getLeftPinnedStyle(header.column)}
                    >
                      {header.isPlaceholder ? null : shouldUseButton ? (
                        <Button
                          variant="ghost"
                          className="flex items-center group p-1"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {sortIcons}
                        </Button>
                      ) : (
                        <div
                          className={
                            isStringHeader
                              ? 'pl-2 flex items-center group'
                              : undefined
                          }
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                interactive={!!onRowClick}
                onClick={
                  onRowClick ? () => onRowClick(row.original) : undefined
                }
                data-testid={`${finalTestId}-row-${row.index}`}
                className={rowClassName?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => {
                  const isLeftPinned = cell.column.getIsPinned() === 'left';

                  return (
                    <TableCell
                      key={cell.id}
                      data-testid={`${finalTestId}-cell-${row.index}-${cell.column.id}`}
                      className={cx(
                        'pl-5',
                        cellClassName,
                        isLeftPinned &&
                          'sticky z-10 bg-default border-r border-subtle',
                      )}
                      style={getLeftPinnedStyle(cell.column)}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {enablePagination && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-subtle">
              Page {table.getState().pagination.pageIndex + 1} of{' '}
              {Math.ceil(
                table.getFilteredRowModel().rows.length /
                  table.getState().pagination.pageSize,
              )}
            </div>
            <div className="flex gap-2">
              <Button
                size="md"
                variant="outlineNeutral"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                size="md"
                variant="outlineNeutral"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  },
) as <TData = unknown>(
  props: DataTableProps<TData> & { ref?: React.Ref<HTMLTableElement> },
) => ReactElement;
